import { useEffect, useState } from "react";
import { listTemplates, deleteTemplate } from "@/lib/api";
import { Trash2, ArrowUpRight, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";

export default function TemplatesPanel({ refreshKey, onLoad }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listTemplates()
      .then((data) => {
        if (alive) setItems(data);
      })
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteTemplate(id);
      setItems((s) => s.filter((x) => x.id !== id));
      toast.success("Template deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return <div className="p-6 mono text-sm text-[#64748B]">Loading templates…</div>;
  }
  if (!items.length) {
    return (
      <div className="p-8 text-center text-[#64748B] text-sm">
        <LayoutTemplate className="w-8 h-8 mx-auto mb-3 opacity-40" />
        No saved templates yet.
        <div className="mono text-[11px] mt-2 opacity-70">
          Save your current cargo + fleet setup as a template to quickly reuse it for repeat client jobs.
        </div>
      </div>
    );
  }

  return (
    <div className="divide-y divide-white/5" data-testid="templates-list">
      {items.map((it) => {
        const d = new Date(it.created_at);
        const cases = it.cases || [];
        const totalUnits = cases.reduce((s, c) => s + (c.quantity || 1), 0);
        const totalWt = cases.reduce((s, c) => s + c.weight_kg * (c.quantity || 1), 0);
        const modeLabel = it.mode === "export" ? "EXPORT" : "DOMESTIC";
        return (
          <div
            key={it.id}
            data-testid={`template-item-${it.id}`}
            onClick={() => onLoad(it)}
            className="p-4 cursor-pointer hover:bg-white/[0.03] transition-colors duration-200 group"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={
                      "mono text-[9px] uppercase tracking-widest px-2 py-0.5 border " +
                      (it.mode === "export"
                        ? "border-blue-500/40 bg-blue-500/10 text-blue-300"
                        : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300")
                    }
                  >
                    {modeLabel}
                  </span>
                  <div className="heading font-semibold truncate">{it.name}</div>
                </div>
                <div className="mono text-[11px] text-[#64748B] mt-1">
                  {d.toLocaleDateString()} · {cases.length} case types · {totalUnits} units
                </div>
                <div className="flex gap-3 mono text-[11px] mt-2 text-[#64748B]">
                  <span>{totalWt.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg</span>
                  <span>{it.vehicles?.length || 0} vehicle types</span>
                  {it.trip?.from_location && (
                    <span className="truncate">{it.trip.from_location} → {it.trip.to_location}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <ArrowUpRight className="w-4 h-4 text-[#2563EB]" />
                <button
                  data-testid={`delete-template-${it.id}`}
                  onClick={(e) => remove(it.id, e)}
                  className="text-slate-400 hover:text-[#EF4444] transition-colors duration-200"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
